import groq from 'groq';
import { CONFIRMATION_TYPE } from './types/forms';
import { portableTextWithReferences } from './portableTextWithReferences';
import { formConfirmationFields, formConfirmationFieldsSimple } from './forms/confirmationFields';
import { generalLink } from './linkQueries';
import { translatedFieldQuery } from './translatedFieldQuery';

// Import existing field queries from modular content blocks
import { blockButtonGroupFields } from '@pkg/modular-blocks/blocks/inner/ButtonGroup';
import { blockPeopleListFields } from '@pkg/modular-blocks/blocks/inner/PeopleList';
import { blockBaseTextFields } from '@pkg/modular-blocks/blocks/inner/BaseText';
import { blockFaqBlockFields } from '@pkg/modular-blocks/blocks/inner/FAQ';
import { blockStatisticsFields } from '@pkg/modular-blocks/blocks/inner/Statistics';
import { blockCalloutFields } from '@pkg/modular-blocks/blocks/inner/Callout';
import { blockTickerFields } from '@pkg/modular-blocks/blocks/inner/Ticker';
import { blockVideoFields } from '@pkg/modular-blocks/blocks/inner/Video';
import { blockProfilePictureFields } from '@pkg/modular-blocks/blocks/inner/ProfilePicture';
import { blockPartnerLogoFields } from '@pkg/modular-blocks/blocks/inner/PartnerLogo';
import { blockTilesFields } from '@pkg/modular-blocks/blocks/inner/Tiles';
import { blockNudgeFields } from '@pkg/modular-blocks/blocks/inner/Nudge';
import { blockSolutionTilesFields } from '@pkg/modular-blocks/blocks/inner/SolutionTiles';
import { blockCampaignCounterFields } from '@pkg/modular-blocks/blocks/inner/CampaignCounter';
import { blockPrivacyControlsSectionFields } from '@pkg/modular-blocks/blocks/outer/PrivacyControls';

// Note: Forms and SalesforceForm blocks need special handling to avoid circular dependencies
// We'll create simplified field queries for them instead of importing their full field queries

// Title fields (no special fields needed)
const titleFields = groq`
  ...,
  title[] {
    ${portableTextWithReferences}
  }
`;

// Listing fields (no special fields needed)
const listingFields = groq``;

const confirmationResolvedTypeField = groq`
  "type": coalesce(
    select(
      type == "${CONFIRMATION_TYPE.AUTOMATED_REDIRECT}" => "${CONFIRMATION_TYPE.AUTOMATED_REDIRECT}",
      defined(contentRedirectUrl) && contentRedirectUrl != "" => "${CONFIRMATION_TYPE.REDIRECT}",
      defined(contentBlocks) && count(contentBlocks) > 0 => "${CONFIRMATION_TYPE.BLOCKS}"
    ),
    type,
    "${CONFIRMATION_TYPE.MESSAGE}"
  ),
  contentRedirectUrl,
`;

// Forms fields (simplified to avoid circular dependency)
const formsFields = groq`
  ...,
  form->{
    _id,
    title,
    submitButtonText,
    fields[] {
      ...,
      richValidationMessage[] {
        ${portableTextWithReferences}
      },
      noteText[] {
        ${portableTextWithReferences}
      }
    },
    confirmation {
      ${formConfirmationFieldsSimple}
    }
  }
`;

// SalesforceForm fields (simplified to avoid circular dependency)
const salesforceFormFields = groq`
  ...,
  salesforceForm->{
    _id,
    title,
    formUrl,
    formOid,
    productInterest,
    submitButtonText,
    fieldsTexts {
      ...,
      noteText[] {
        ${portableTextWithReferences}
      }
    },
    platformOptions {
      ...,
      platformOptionsValidationMessage[] {
        ${portableTextWithReferences}
      }
    },
    blockerInputTexts {
      ...,
      blockerValidationMessage[] {
        ${portableTextWithReferences}
      }
    }
  }
`;

// All inner modular content blocks for form confirmations
const formConfirmationBlocks = groq`
  ...,
  _type == "modularContentBlocks.inner.buttonGroup" => {
    ${blockButtonGroupFields}
  },
  _type == "modularContentBlocks.inner.people" => {
    ${blockPeopleListFields}
  },
  _type == "modularContentBlocks.inner.title" => {
    ${titleFields}
  },
  _type == "modularContentBlocks.inner.baseText" => {
    ${blockBaseTextFields}
  },
  _type == "modularContentBlocks.inner.faq" => {
    ${blockFaqBlockFields}
  },
  _type == "modularContentBlocks.inner.statistics" => {
    ${blockStatisticsFields}
  },
  _type == "modularContentBlocks.inner.callout" => {
    ${blockCalloutFields}
  },
  _type == "modularContentBlocks.inner.ticker" => {
    ${blockTickerFields}
  },
  _type == "modularContentBlocks.inner.video" => {
    ${blockVideoFields}
  },
  _type == "modularContentBlocks.inner.profilePicture" => {
    ...,
    "person": person->
  },
  _type == "modularContentBlocks.inner.partnerLogo" => {
    ...,
    partnerLogo->{
      ...,
      ${translatedFieldQuery("partnerName")},
      ${translatedFieldQuery("partnerLogo")},
    }
  },
  _type == "modularContentBlocks.inner.listing" => {
    ${listingFields}
  },
  _type == "modularContentBlocks.inner.tiles" => {
    ${blockTilesFields}
  },
  _type == "modularContentBlocks.inner.nudge" => {
    ${blockNudgeFields}
  },
  _type == "modularContentBlocks.inner.solutionTiles" => {
    ${blockSolutionTilesFields}
  },
  _type == "modularContentBlocks.inner.campaignCounter" => {
    ${blockCampaignCounterFields}
  },
  _type == "modularContentBlocks.outer.privacyControls" => {
    ${blockPrivacyControlsSectionFields}
  },
  _type == "modularContentBlocks.inner.forms" => {
    ${formsFields}
  },
  _type == "modularContentBlocks.inner.salesforceForms" => {
    ${salesforceFormFields}
  }
`;

export const formWithReferences = groq`
  ...,
  notifications[] {
    _type,
    _key,
  },
  fields[] {
    ...,
    richValidationMessage[] {
      ${portableTextWithReferences}
    },
    noteText[] {
      ${portableTextWithReferences}
    }
  },
  confirmation {
    ${formConfirmationFields(formConfirmationBlocks)}
  }
`;
